import { AudioFormat, AudioChunk } from '../types/audio';

/**
 * Audio processing service for Web Audio API integration
 * Handles PCM encoding and audio format conversion
 */
export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;
  private sequenceNumber: number = 0;
  private onAudioChunk?: (chunk: AudioChunk) => void;

  /**
   * Initialize audio processing with the specified format
   */
  async initialize(format: AudioFormat, onChunk?: (chunk: AudioChunk) => void): Promise<boolean> {
    try {
      // Create audio context without specifying sample rate to use browser's native rate
      // This avoids sample rate mismatch errors when connecting to media streams
      this.audioContext = new AudioContext();

      // Wait for audio context to be running
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      console.log(`🎵 [AudioProcessor] AudioContext created with sample rate: ${this.audioContext.sampleRate}Hz (requested: ${format.sampleRate}Hz)`);

      this.onAudioChunk = onChunk;
      this.sequenceNumber = 0;

      return true;
    } catch (error) {
      console.error('Failed to initialize audio processor:', error);
      return false;
    }
  }

  /**
   * Start processing audio from a media stream
   */
  startProcessing(stream: MediaStream, format: AudioFormat): boolean {
    if (!this.audioContext) {
      console.error('Audio processor not initialized');
      return false;
    }

    try {
      // Create media stream source
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream);

      // Create analyser for audio level monitoring
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;

      // Create script processor for audio data extraction
      // Buffer size should be power of 2, between 256 and 16384
      const bufferSize = this.getOptimalBufferSize(format.sampleRate);
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        bufferSize,
        format.channels,
        format.channels
      );

      // Set up audio processing callback
      this.scriptProcessor.onaudioprocess = (event) => {
        this.processAudioBuffer(event.inputBuffer, format);
      };

      // Connect audio nodes
      this.mediaStreamSource.connect(this.analyser);
      this.analyser.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      return true;
    } catch (error) {
      console.error('Failed to start audio processing:', error);
      return false;
    }
  }

  /**
   * Stop audio processing and cleanup resources
   */
  stopProcessing(): void {
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Get current audio level (0-100)
   */
  getAudioLevel(): number {
    if (!this.analyser) return 0;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    // Calculate RMS (Root Mean Square) for audio level
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / dataArray.length);
    
    // Convert to percentage (0-100)
    return Math.min(100, (rms / 255) * 100);
  }

  /**
   * Check if audio context is running
   */
  isRunning(): boolean {
    return this.audioContext?.state === 'running';
  }

  /**
   * Get optimal buffer size based on sample rate
   */
  private getOptimalBufferSize(sampleRate: number): number {
    // Target ~100ms of audio per buffer for real-time processing
    const targetBufferMs = 100;
    const targetSamples = (sampleRate * targetBufferMs) / 1000;
    
    // Find nearest power of 2
    let bufferSize = 256;
    while (bufferSize < targetSamples && bufferSize < 16384) {
      bufferSize *= 2;
    }
    
    return bufferSize;
  }

  /**
   * Process audio buffer and convert to PCM chunks
   */
  private processAudioBuffer(buffer: AudioBuffer, format: AudioFormat): void {
    if (!this.onAudioChunk) return;

    try {
      // Get audio data from first channel
      const channelData = buffer.getChannelData(0);
      
      // Convert Float32Array to PCM16 ArrayBuffer
      const pcmData = this.convertToPCM16(channelData, format.bitsPerSample);

      const chunk: AudioChunk = {
        data: pcmData,
        timestamp: Date.now(),
        sequenceNumber: this.sequenceNumber++,
      };

      this.onAudioChunk(chunk);
    } catch (error) {
      console.error('Error processing audio buffer:', error);
    }
  }

  /**
   * Convert Float32Array to PCM ArrayBuffer
   */
  private convertToPCM16(float32Data: Float32Array, _bitsPerSample: number): ArrayBuffer {
    const arrayBuffer = new ArrayBuffer(float32Data.length * 2); // 16-bit = 2 bytes per sample
    const view = new DataView(arrayBuffer);

    for (let i = 0; i < float32Data.length; i++) {
      // Clamp float32 value to [-1, 1] range
      const sample = Math.max(-1, Math.min(1, float32Data[i]));
      
      // Convert to 16-bit signed integer
      const int16Sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      
      // Write as little-endian 16-bit integer
      view.setInt16(i * 2, int16Sample, true);
    }

    return arrayBuffer;
  }

  /**
   * Validate audio format compatibility
   */
  static validateFormat(format: AudioFormat): { valid: boolean; error?: string } {
    // Check sample rate
    const supportedSampleRates = [16000, 44100, 48000];
    if (!supportedSampleRates.includes(format.sampleRate)) {
      return {
        valid: false,
        error: `Unsupported sample rate: ${format.sampleRate}Hz. Supported rates: ${supportedSampleRates.join(', ')}Hz`,
      };
    }

    // Check channels
    if (format.channels < 1 || format.channels > 2) {
      return {
        valid: false,
        error: `Unsupported channel count: ${format.channels}. Supported: 1 (mono) or 2 (stereo)`,
      };
    }

    // Check bits per sample
    if (format.bitsPerSample !== 16) {
      return {
        valid: false,
        error: `Unsupported bits per sample: ${format.bitsPerSample}. Only 16-bit is supported`,
      };
    }

    return { valid: true };
  }

  /**
   * Get browser's preferred audio format
   */
  static async getBrowserPreferredFormat(): Promise<AudioFormat> {
    try {
      // Create temporary audio context to get browser's preferred sample rate
      const tempContext = new AudioContext();
      const sampleRate = tempContext.sampleRate;
      await tempContext.close();

      // Use browser's preferred sample rate if supported, otherwise default to 16kHz
      const preferredSampleRate = [16000, 44100, 48000].includes(sampleRate) ? sampleRate : 16000;

      return {
        sampleRate: preferredSampleRate,
        channels: 1, // Mono for transcription
        bitsPerSample: 16,
      };
    } catch (error) {
      console.warn('Could not determine browser preferred format, using defaults:', error);
      return {
        sampleRate: 16000,
        channels: 1,
        bitsPerSample: 16,
      };
    }
  }
}